// pdfUtils.js
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

/* -------------------------------------------------------
   WAIT FOR IMAGES
------------------------------------------------------- */
export async function fn_waitForImages(el) {
  if (!el) return;
  const images = Array.from(el.querySelectorAll("img"));

  const promises = images.map(img => {
    if (img.complete) return Promise.resolve();
    return new Promise(resolve => {
      img.onload = resolve;
      img.onerror = resolve;
    });
  });

  await Promise.all(promises);
}
function replaceCanvasWithImage(rootEl) {
  const canvases = rootEl.querySelectorAll("canvas");

  canvases.forEach((canvas) => {
    try {
      const dataURL = canvas.toDataURL("image/png");

      const img = document.createElement("img");
      img.src = dataURL;
      img.width = canvas.width;
      img.height = canvas.height;
      img.style.display = "block";

      canvas.parentNode.replaceChild(img, canvas);
    } catch (e) {
      console.error("Canvas conversion failed:", e);
    }
  });
}

export async function fn_htmlToPdfBuffer(element, { isLandscape = false } = {}, replaceCanvas=false) {
  if (!element) return null;

  // A4 dimensions in mm
  const pdfWidth = isLandscape ? 297 : 210;
  const pdfHeight = isLandscape ? 210 : 297;
  
  
  // replace existing canvas first
  if(replaceCanvas) replaceCanvasWithImage(element);

  // Render DOM to canvas
  const canvas = await html2canvas(element, {
    scale: 2,                // High quality
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false
  });

  const imgData = canvas.toDataURL("image/png");

  // Canvas dimensions in px
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;

  // jsPDF instance
  const pdf = new jsPDF({
    orientation: isLandscape ? "l" : "p",
    unit: "mm",
    format: "a4"
  });

  // Convert px → mm (jsPDF uses mm)
  const pageWidthMM = pdfWidth;
  const pageHeightMM = pdfHeight;

  const pxPerMM = canvasWidth / pageWidthMM;
  const pageHeightPx = pageHeightMM * pxPerMM;

  let renderedHeight = 0;

  // Slice canvas into A4-height chunks
  while (renderedHeight < canvasHeight) {
    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvasWidth;
    sliceCanvas.height = Math.min(pageHeightPx, canvasHeight - renderedHeight);

    const sliceCtx = sliceCanvas.getContext("2d");

    sliceCtx.drawImage(
      canvas,
      0,
      renderedHeight,
      canvasWidth,
      sliceCanvas.height,
      0,
      0,
      canvasWidth,
      sliceCanvas.height
    );

    const sliceImg = sliceCanvas.toDataURL("image/png");

    // Add slice to PDF
    pdf.addImage(
      sliceImg,
      "PNG",
      0,
      0,
      pageWidthMM,
      (sliceCanvas.height / pxPerMM)
    );

    renderedHeight += pageHeightPx;

    if (renderedHeight < canvasHeight) {
      pdf.addPage();
    }
  }

  //return pdf.output("arraybuffer");
  const buffer = pdf.output("arraybuffer");
  const pageCount = await fn_pdfBufferPageCount(buffer);
  return {buffer , pageCount };
  
  return
}

/* -------------------------------------------------------
   Element → PDF BUFFER (jsPDF)
------------------------------------------------------- */
async function fn_elementToPdfBuffer(el, isLandscape) {
  const canvas = await html2canvas(el, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff"
  });

  const imgData = canvas.toDataURL("image/png");

  const doc = new jsPDF({
    orientation: isLandscape ? "l" : "p",
    unit: "mm",
    format: "a4"
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = 0;

  doc.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;

  while (heightLeft > 0) {
    doc.addPage();
    position = heightLeft - imgHeight;
    doc.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  return doc.output("arraybuffer");
}

/* -------------------------------------------------------
   MERGE PDF BUFFER INTO FINAL DOC
------------------------------------------------------- */
export async function fn_mergePdfBuffer(finalDoc, buffer, expectedPages) {
  const sourceDoc = await PDFDocument.load(buffer);
  const pageCount = sourceDoc.getPageCount();

  const sliceEnd = expectedPages !== undefined
    ? Math.min(expectedPages, pageCount)
    : pageCount;

  const indices = sourceDoc.getPageIndices().slice(0, sliceEnd);

  if (indices.length > 0) {
    const copied = await finalDoc.copyPages(sourceDoc, indices);
    copied.forEach(p => finalDoc.addPage(p));
  }
}

/* -------------------------------------------------------
   COMPUTE EXPECTED PAGES
------------------------------------------------------- */
export function fn_computeExpectedPages(sectionEl, isLandscape) {
  if (!sectionEl) return 1;

  const rect = sectionEl.getBoundingClientRect();
  const a4Ratio = isLandscape
    ? (211.0 / 296.83)
    : (296.83 / 211.0);

  const pageHeightPx = rect.width * a4Ratio;
  return Math.max(1, Math.round(rect.height / pageHeightPx));
}

/* -------------------------------------------------------
   RESOLVE START PAGE NUMBER
------------------------------------------------------- */
export function fn_resolveStartPage(section, pageStartMap) {
  if (!pageStartMap) return undefined;

  if (section.key === "toc") {
    return pageStartMap["frontPageExtra"];
  }

  return pageStartMap[`${section.key}Extra`];
}

/* -------------------------------------------------------
   BASE64 → ARRAYBUFFER
------------------------------------------------------- */
export function fn_decodeBase64Pdf(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/* -------------------------------------------------------
   EMBED CUSTOM PDF PAGE WITH BANNERS + PAGE NUMBERS
------------------------------------------------------- */
export async function fn_embedCustomPdfPage({
  finalDoc,
  donorPage,
  helveticaFont,
  A4_WIDTH,
  A4_HEIGHT,
  BANNER_HEIGHT,
  TEXT_COLOR,
  file,
  startPageNum
}) {
  const { width, height } = donorPage.getSize();
  const CONTENT_PADDING = 5;
  const availableHeight = A4_HEIGHT - (BANNER_HEIGHT * 2) - (CONTENT_PADDING * 2);
  const aspectRatio = width / height;

  let renderWidth = A4_WIDTH;
  let renderHeight = renderWidth / aspectRatio;

  if (renderHeight > availableHeight) {
    renderHeight = availableHeight;
    renderWidth = renderHeight * aspectRatio;
  }

  const x = (A4_WIDTH - renderWidth) / 2;
  const y = BANNER_HEIGHT + CONTENT_PADDING + ((availableHeight - renderHeight) / 2);

  const newPage = finalDoc.addPage([A4_WIDTH, A4_HEIGHT]);
  const embeddedPage = await finalDoc.embedPage(donorPage);

  newPage.drawPage(embeddedPage, { x, y, width: renderWidth, height: renderHeight });

  const headerColor = file.headerRGB ? rgb(...file.headerRGB) : rgb(0.9, 0.9, 0.9);
  const footerColor = file.footerRGB ? rgb(...file.footerRGB) : rgb(0.9, 0.9, 0.9);

  newPage.drawRectangle({
    x: 0, y: A4_HEIGHT - BANNER_HEIGHT,
    width: A4_WIDTH, height: BANNER_HEIGHT,
    color: headerColor
  });
  
}
/* -------------------------------------------------------
 * Returns the number of pages in a PDF buffer.
 * @param {ArrayBuffer|Uint8Array} buffer - PDF data from jsPDF or uploaded file
 * @returns {Promise<number>}
------------------------------------------------------- */ 
export async function fn_pdfBufferPageCount(buffer) {
  if (!buffer) return 0;
  console.log("PDF buffer size:", buffer.byteLength);
  try {
    const pdf = await PDFDocument.load(buffer);

    // Remove trailing blank pages
    const pages = pdf.getPages();
    const last = pages[pages.length - 1];

    if (last.getWidth() === 0 || last.getHeight() === 0) {
      pdf.removePage(pages.length - 1);
    }

    return pdf.getPageCount();
  } catch (err) {
    console.error("Failed to read PDF page count", err);
    return 0;
  }
}

export function fn_pdfPageCount(sectionEl) {
  if (!sectionEl) return 0;
  return sectionEl.querySelectorAll(".page-unit").length;
}

/* -------------------------------------------------------
 * Loads all fonts defined in fontAssets and returns:
 * [
 *   { name: "Roboto", style: "bold", base64: "..." },
 *   { name: "Roboto", style: "regular", base64: "..." }
 * ]
------------------------------------------------------- */
export async function fn_loadAllFonts(fontAssets) {
  if (!fontAssets || typeof fontAssets !== "object") {
    console.error("loadAllFonts: invalid fontAssets");
    return [];
  }

  const loaded = [];
  const tasks = [];

  const processFont = async (name, style, path) => {
    try {
      const response = await fetch(path);
      const blob = await response.blob();

      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = () => {
          try {
            const encoded = reader.result.split(",")[1];
            resolve(encoded);
          } catch (err) {
            console.error(`Failed to process font ${name} ${style}`, err);
            resolve(null);
          }
        };

        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      if (base64) {
        loaded.push({ name, style, base64 });
      }
    } catch (err) {
      console.error(`Failed to fetch font ${name} ${style}`, err);
    }
  };

  for (const [name, variations] of Object.entries(fontAssets)) {
    for (const [style, path] of Object.entries(variations)) {
      tasks.push(processFont(name, style, path));
    }
  }

  await Promise.all(tasks);
  return loaded;
}
export function fn_binaryStringToArrayBuffer(binaryString) {
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i) & 0xff;
  }
  return bytes.buffer;
}
export function fn_normalizePdfBuffer(pdfData) {
  // Already ArrayBuffer or Uint8Array → OK
  if (pdfData instanceof ArrayBuffer) return pdfData;
  if (pdfData instanceof Uint8Array) return pdfData.buffer;

  // Raw binary PDF string (starts with %PDF)
  if (typeof pdfData === "string" && pdfData.startsWith("%PDF")) {
    return fn_binaryStringToArrayBuffer(pdfData);
  }

  // Base64 PDF string
  if (typeof pdfData === "string") {
    const cleaned = pdfData.replace(/^data:application\/pdf;base64,/, "");
    const binary = atob(cleaned);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  throw new Error("Unsupported PDF format");
}
async function m_addPdfPageNumbers(finalDoc) {
  const font = await finalDoc.embedFont(StandardFonts.Helvetica);
  const pageCount = finalDoc.getPageCount();

  for (let i = 0; i < pageCount; i++) {
    const page = finalDoc.getPage(i);
    const { width, height } = page.getSize();

	if(![0,1,pageCount-1].includes(i)) {
         page.drawText(`Page ${i - 1} of ${pageCount-3}`, {
            x: width / 2 - 30,
            y: 20,
            size: 10,
            font,
            color: rgb(0.2, 0.2, 0.2),
		});
	  }
    }
  }

/*
 onst buffer = await fn_htmlToPdfBuffer(sectionEl, { isLandscape });
const pageCount = await fn_pdfPageCount(buffer);

console.log("Section pages:", pageCount);
Store it for your Table of Contents:
js
this.pageCounts[section.key] = pageCount;

[
  { id: 'frontPage', pageStart: 1, pageCount: 1 },
  { id: 'orgChart', pageStart: 2, pageCount: 3 },
  { id: 'resume', pageStart: 5, pageCount: 2 },
  { id: 'uploadedPdf_abc', pageStart: 7, pageCount: 4 },
  ...
]
calculate page count
let runningPage = 1;
const tocEntries = [];

for (const section of this.steps) {

  if (section.type === "uploadedPdf") {

    let pdfData = await this.$store.dispatch("getProposalFile", fileName);
    pdfData = normalizePdfBuffer(pdfData);

    const pageCount = await pdfPageCount(pdfData);

    tocEntries.push({
      id: section.key,
      name: section.title,
      pageStart: runningPage,
      pageCount
    });

    runningPage += pageCount;

    const extraDoc = await PDFDocument.load(pdfData);
    const donorPages = await finalDoc.copyPages(extraDoc, extraDoc.getPageIndices());
    donorPages.forEach(p => finalDoc.addPage(p));

  } else {

    const el = this.$refs.proposalContainer.querySelector(`.${section.class}`);
    if (!el) continue;

    const buffer = await elementToPdfBuffer(el, isLandscape);
    const normalized = normalizePdfBuffer(buffer);

    const pageCount = await pdfPageCount(normalized);

    tocEntries.push({
      id: section.key,
      name: section.title,
      pageStart: runningPage,
      pageCount
    });

    runningPage += pageCount;

    await fn_mergePdfBuffer(finalDoc, normalized, pageCount);
  }
}

ONCLONE
html2canvas(elementById[index], {
    onclone: function (documentClone) {

        var headnode = document.createElement("h1");
        var textnode = document.createTextNode('Hello World');  
        headnode.appendChild(textnode);

        documentClone.querySelector('#list').prepend(headnode)
        (<HTMLElement>documentClone.querySelector('body')).style.padding = '30px';
      }
  }).then(canvas => {
     ...
})


*/

